import React, { useEffect, useRef, useState } from 'react'
import { Filter as FilterIcon } from 'lucide-react'
import {
  SubHeaderContainer,
  LeftSide,
  RightSide,
  SectionName,
  TabsDiv,
  Tab,
  RefreshIcon,
  RefreshIconContainer,
  FilterWrapper,
  OptionsButton,
  ButtonCreate,
} from '../styles/subheader-style'
import refreshAct from '../../assets/refresh-active.svg'
import FilterMenu from '../SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../SubHeaderFilter/AppliedFiltersDisplay'
import { PageHeaderProps } from './types'
import { useTableExportContext } from './TableExportContext'
import { HeaderExportButton } from './styles'

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleTooltip,
  tabs,
  activeTab,
  onTabChange,
  onReload,
  isLoading,
  filterHeaders,
  appliedFilters,
  onApplyFilter,
  onRemoveFilter,
  onClearAllFilters,
  onExport,
  exportLabel = 'Exportar excel',
  createButton,
  leftExtra,
  rightExtra,
  extraActions,
}) => {
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)
  const exportContext = useTableExportContext()

  const registerPageHeader = exportContext?.registerPageHeader
  const unregisterPageHeader = exportContext?.unregisterPageHeader

  useEffect(() => {
    if (!registerPageHeader || !unregisterPageHeader) return
    registerPageHeader()
    return () => unregisterPageHeader()
  }, [registerPageHeader, unregisterPageHeader])

  const tableExport = exportContext?.registration ?? null

  const showTabs = !!tabs && tabs.length > 0
  const showFilters = !!filterHeaders && filterHeaders.length > 0 && !!onApplyFilter
  const showAppliedFilters = !!appliedFilters && appliedFilters.length > 0 && !!onRemoveFilter && !!onClearAllFilters

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName title={titleTooltip}>{title}</SectionName>
          {showTabs && (
            <TabsDiv>
              {tabs!.map((tab, index) => (
                <Tab
                  key={tab.key}
                  $isFirst={index === 0}
                  $isLast={index === tabs!.length - 1}
                  $isActive={tab.key === activeTab}
                  onClick={() => {
                    if (tab.key !== activeTab) {
                      onTabChange?.(tab.key)
                    }
                  }}
                >
                  {tab.icon && (typeof tab.icon === 'string' ? <img src={tab.icon} alt={`${tab.label}-icon`} /> : tab.icon)}
                  {tab.label}
                </Tab>
              ))}
            </TabsDiv>
          )}
          {leftExtra}
        </LeftSide>
        <RightSide>
          {rightExtra}
          {onReload && (
            <RefreshIconContainer onClick={onReload} title="Atualizar">
              <RefreshIcon src={refreshAct} alt="Atualizar" $spinning={!!isLoading} />
            </RefreshIconContainer>
          )}
          {showFilters && (
            <FilterWrapper>
              <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                <FilterIcon size={18} />
              </OptionsButton>
              {isFilterMenuOpen && (
                <FilterMenu
                  headers={filterHeaders!}
                  onApplyFilter={(filter) => {
                    onApplyFilter!(filter)
                    setIsFilterMenuOpen(false)
                  }}
                  onClose={() => setIsFilterMenuOpen(false)}
                  anchorRef={filterButtonRef}
                  filters={appliedFilters}
                />
              )}
            </FilterWrapper>
          )}
          {extraActions}
          {onExport ? (
            <HeaderExportButton onClick={() => onExport()}>{exportLabel}</HeaderExportButton>
          ) : tableExport ? (
            <HeaderExportButton
              onClick={() => tableExport.onExport()}
              disabled={tableExport.isExporting || tableExport.disabled}
            >
              {tableExport.isExporting ? 'Exportando...' : 'Exportar excel'}
            </HeaderExportButton>
          ) : null}
          {createButton && <ButtonCreate onClick={createButton.onClick}>+ {createButton.label}</ButtonCreate>}
        </RightSide>
      </SubHeaderContainer>
      {showAppliedFilters && (
        <AppliedFiltersDisplay
          filters={appliedFilters!}
          onRemoveFilter={onRemoveFilter!}
          onClearAll={onClearAllFilters!}
        />
      )}
    </>
  )
}

export default PageHeader
